import moment from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import Calendar from './Calendar'
import CalendarSlim from './calendar-slim/CalendarSlim'
import useTranslation from '../../../utils/translation'
import Error from '../error/Error'
import features from '../features'
import settings from '../settings'
import appInsights, { SeverityLevel } from '../logging/appInsights'
import { isDesigner } from '../../../utils/initValues'
import { useCalendar } from '../../hooks/appointment/useSbmCalendar'
import { getCalendar } from '../../../utils/badaAppointmentApi'

const MAX_DAYS = 60
const PAGE_SIZE = 10
const END_RANGE = moment()
  .add(PAGE_SIZE - 1, 'days')
  .toISOString()
  .split('T')[0]

export default function CalendarHOC2({
  meetingTypeId,
  storeId,
  locationId,
  sbmInstance,
  onChange,
  serviceData,
  noSlotMeetingType,
  selectedServiceProvider,
  flow,
  serviceDuration,
}) {
  const t = useTranslation('app.slot-selection')
  const [calendarDays, setCalendarDays] = useState([])
  const [moreCalendarDays, setMoreCalendarDays] = useState([])
  const [initialDays, setInitialDays] = useState([])
  const [startDate, setStartDate] = useState(null)
  const [allFetched, setAllFetched] = useState(false)
  const [allDataLoaded, setAllDataLoaded] = useState(false)
  const [noDataToShow, setNoDataToShow] = useState(false)
  const selectedServiceProviderId = useMemo(() => {
    if (flow === 'followup') {
      return selectedServiceProvider?.id == null
        ? 0
        : selectedServiceProvider?.id
    }

    return selectedServiceProvider?.id
  }, [selectedServiceProvider, flow])
  const {
    data,
    isLoading: loading,
    error,
  } = useCalendar(
    {
      locationId,
      serviceId: meetingTypeId,
      fromDate: startDate,
      toDate: END_RANGE,
      subsystem: sbmInstance,
      serviceProviderId: selectedServiceProviderId,
      serviceDuration,
      style: isDesigner ? 'flexible' : 'dense',
    },
    {
      skip: allFetched || !startDate,
    }
  )

  function reset() {
    setCalendarDays([])
    setInitialDays([])

    const startDateTz = moment
      .tz(settings.ianaTimeZoneId)
      .format()
      .split('T')[0]

    setStartDate(startDateTz)
    setAllFetched(false)
    setAllDataLoaded(false)
    setNoDataToShow(false)
  }

  useEffect(() => {
    reset()
  }, [meetingTypeId, selectedServiceProviderId, serviceDuration])

  const loadMoreData = async () => {
    const start = moment().add(PAGE_SIZE, 'days').toISOString().split('T')[0]
    const end = moment().add(MAX_DAYS, 'days').toISOString().split('T')[0]

    const moreData = await getCalendar({
      locationId,
      serviceId: meetingTypeId,
      fromDate: start,
      toDate: end,
      subsystem: sbmInstance,
      serviceProviderId: selectedServiceProviderId,
      serviceDuration,
      style: isDesigner ? 'flexible' : 'dense',
    })

    if (moreData) {
      setMoreCalendarDays(moreData)
      setAllDataLoaded(true)
    }
  }

  useEffect(() => {
    setCalendarDays([...calendarDays, ...moreCalendarDays])
  }, [moreCalendarDays])

  useEffect(() => {
    if (data) {
      if (data.length === 0) {
        setNoDataToShow(true)
        setAllDataLoaded(true)
        setAllFetched(true)
      } else {
        setCalendarDays(data)

        if (!allFetched) {
          loadMoreData()
          setAllFetched(true)
        }
      }
    }
  }, [data])

  useEffect(() => {
    setInitialDays(calendarDays)
  }, [calendarDays])

  if (error || (!loading && !calendarDays)) {
    appInsights.trackException({
      exception: new Error(
        `Error in calendar2 for brand ${settings.brand}, meetingTypeId ${meetingTypeId}, storeId ${storeId}, locationId ${locationId}, sbmInstance ${sbmInstance}, error: ${error}`
      ),
      severityLevel: SeverityLevel.Error,
    })
    return <Error header={t('error.title')} message={t('error.message')} />
  }

  const hideSameDaySlots =
    settings.disableSameDayBooking ||
    (Array.isArray(settings.disableSameDayBookingSubsystems) &&
      settings.disableSameDayBookingSubsystems.includes(sbmInstance))

  const filteredDays =
    hideSameDaySlots && !isDesigner
      ? calendarDays.filter((d) => moment(d.day) > moment())
      : calendarDays

  return (
    <>
      {features.newStyleCalendar ? (
        <CalendarSlim
          loading={initialDays.length === 0 && !noDataToShow}
          allDataLoaded={allDataLoaded}
          days={filteredDays || []}
          allDays={filteredDays || []}
          startDate={startDate}
          endDate={END_RANGE}
          onChange={onChange}
          serviceData={serviceData}
          meetingTypeId={meetingTypeId}
          showAltStore
        />
      ) : (
        <Calendar
          loading={initialDays.length === 0 && !noDataToShow}
          allDataLoaded={allDataLoaded}
          days={filteredDays || []}
          allDays={filteredDays || []}
          startDate={startDate}
          endDate={END_RANGE}
          onChange={onChange}
          serviceData={serviceData}
          noSlotMeetingType={noSlotMeetingType}
          meetingTypeId={meetingTypeId}
        />
      )}
    </>
  )
}
